/**
 * Policy for which feature flags are synced to Amplitude as user properties.
 *
 * Both flag transports funnel through here before calling `identify.set()`:
 * - PostHog:  `setFeatureFlagUserProperties`  (amplitude-feature-flag-properties.ts)
 * - Flagsmith: `setFlagsmithFlagUserProperties` (amplitude-flagsmith-flag-properties.ts)
 *
 * Why this exists: Amplitude caps user properties per user, and syncing every
 * flag overruns that cap. This module is the single place that decides what
 * gets through, so the two transports can never drift.
 *
 * Current shape: a BLOCKLIST. Default is "sync everything" minus anything
 * listed here. Flip to an allowlist later if the set of flags we want turns out
 * to be smaller than the set we don't.
 *
 * Seed set: every flag retired in `docs/feature-flag-cleanups.md`. The code no
 * longer reads these, but they still surface through PostHog
 * `getFlagVariants()` / Flagsmith `getAllFlags()` (frozen desktop bundles still
 * evaluate them; the consoles still hold them until each is disabled/deleted),
 * so without this they keep consuming Amplitude user-property slots for nothing.
 * When you retire a flag, add its key here in the same PR as the cleanup-log row.
 *
 * Keys are the RAW flag keys (no `flag-` prefix — the transports add that).
 * Keys are unique across PostHog and Flagsmith, so one set covers both.
 * Sorted for greppability and clean merges.
 */
export const AMPLITUDE_FLAG_SYNC_BLOCKLIST: ReadonlySet<string> = new Set([
  'debug-tauri-updater',
  'desktop-app-update-visibility',
  'desktop-use-v2-recorder',
  'disable-coding-autogen-for-inactive-users',
  'new_upload_file_to_context',
  'release-allow-adding-form-templates-to-community',
  'release-anonymous-dictation',
  'release-apps-rust-http2-upgraded-desktop',
  'release-athena-new-framework',
  'release-attribution-url-tracking',
  'release-auth-auth0-db-login-fe',
  'release-auth-auth0-social-login-google',
  'release-blur-screen-signup',
  'release-chat-message-queue',
  'release-chat-search-backend',
  'release-check-token-on-tab-focus',
  'release-clinical-considerations-panel',
  'release-cliniko-new-framework',
  'release-comms-enable-call-summary-pdf-download',
  'release-comms-kb-learning',
  'release-custom-consent-text',
  'release-dark-mode',
  'release-default-telehealth-mode',
  'release-desktop-dictation-onboarding',
  'release-dictate-everywhere',
  'release-dictation-hotkey-settings',
  'release-dictation-snippets',
  'release-dictation-stop-delay',
  'release-dictation-tone-settings',
  'release-display-language-dropdown',
  'release-display-system-version',
  'release-dom-patch',
  'release-epic-push-note-options',
  'release-evidence-auto-mode',
  'release-evidence-enable-jurisdiction-labelling',
  'release-evidence-onboarding-modal',
  'release-experimental-design-system',
  'release-fga-direct-sharing',
  'release-follow-up-questions',
  'release-global-template-library-m1',
  'release-growth-deprecate-evidence-plus',
  'release-growth-learn-basics-english-only',
  'release-guess-user-region-from-timezone',
  'release-health-direct-disable-template-selection',
  'release-health-direct-show-section-dropdown-in-sidebar',
  'release-inbox-enable-category-display',
  'release-inbox-enable-manual-upload',
  'release-integration-partners',
  'release-integrations-enable-last-sync-note-indicator',
  'release-integrations-enable-sharepoint-for-evidence',
  'release-japan-region-jp-az1',
  'release-letter-flow',
  'release-letter-sender-fax',
  'release-magentus-new-framework',
  'release-new-billing-options-ui',
  'release-notifications-popover',
  'release-operational-dashboard',
  // Not retired — superseded by `release-patient-summary`, and deliberately
  // still live in Flagsmith and PostHog to serve frozen desktop bundles. No
  // current build reads it, and both transports would fight over its
  // `flag-` property, so it stays out of the Amplitude sync.
  'release-patient-summary-sms',
  'release-persistent-patient-context',
  'release-phone-number-in-signup-de',
  'release-phone-number-in-signup-za',
  'release-prechart-redesign',
  'release-print',
  'release-quickest-recording',
  'release-rbac-role-id',
  'release-rbac-split-4',
  'release-remove-specialty-groupings',
  'release-returning-user-redirect',
  'release-review-confirmation-modal',
  'release-rpa-overlay-render-stream',
  'release-rpa-rust-executor',
  'release-scribe-correct-transcription-connectivity-check',
  'release-scribe-create-button-chat-first-dropdown',
  'release-scribe-create-button-split-dropdown',
  'release-scribe-dictation-onboarding-flow',
  'release-scribe-disable-t11n-transcript-polling',
  'release-scribe-enable-auto-mode-note',
  'release-scribe-enable-dictation-log',
  'release-scribe-enable-dictation-log-backend',
  'release-scribe-enable-dictation-log-opt-out',
  'release-scribe-enable-dictation-log-stats',
  'release-scribe-enable-mini-mode',
  'release-scribe-enable-offline-connectivity-alert',
  'release-scribe-enable-onboarding-document-selection',
  'release-scribe-enable-onboarding-extra-markets',
  'release-scribe-enable-onboarding-mic-permissions',
  'release-scribe-enable-rpa-agent',
  'release-scribe-enable-template-author-grouping',
  'release-scribe-enable-transcribe-chunk-v2',
  'release-scribe-enable-workflows',
  'release-scribe-parallel-start-prep',
  'release-scribe-workspace-hydration-gate',
  'release-session-linking-desktop',
  'release-session-revocation',
  'release-source-control-badges',
  'release-source-control-v2',
  'release-specialty-in-organisation-survey',
  'release-table-support',
  'release-task-enable-due-date-and-ui',
  'release-template-pinning',
  'release-template-sharing',
  'release-universal-dictation-csat',
  'release-user-management-page',
  'release-web-collections',
  'release-web-tab-sharing',
  'replace-region-in-url',
  'reverse-modal',
  'side-by-side-skip-selection-button',
  'v2-redirect',
  'wav-encoder',
  'windows-desktop-process-driven-audio',
]);

/**
 * Whether a flag should be synced to Amplitude as a user property.
 *
 * @param flagKey - Raw flag key, without the `flag-` prefix.
 */
export function shouldSyncFlagToAmplitude(flagKey: string): boolean {
  return !AMPLITUDE_FLAG_SYNC_BLOCKLIST.has(flagKey);
}
